/*
 * router Sagas
 *
 * @version 1.0.0
 * @author Stefano Servodidio
 * @since 15-Jul-2024
 */

import { put, all, select, takeLatest } from 'redux-saga/effects'
import { doResetOrderManagement } from 'Route/OrderManagement/modules/OrderManagementActions'
import * as formikActions from 'Store/actions/formik'
import { doResetOperations } from 'Store/actions/operations'
import { doResetPblGenerateResult } from 'Route/CreatePayByLinkPlus/modules/CreatePayByLinkPlusActions'
import { doResetPtcGenerateResult } from 'Route/PayToCard/modules/PayToCardActions'
import { doResetBookings } from 'Route/BookingManagement/modules/BookingManagementActions'
import { getReduxLocation, getSelectedTerminalId } from 'Store/selectors'
import { doPopup } from 'Store/actions/popup'
import { routesList } from 'Schemas/routesList'
import { doSetActiveTabIndex } from 'Route/TerminalsManagement/modules/TerminalsManagementActions'

export function* routeChangeSaga() {
  // reset popup state when navigating between routes
  yield put(doPopup(''))

  const { location, previousLocations, basename } = yield select(getReduxLocation)
  const previous = previousLocations?.[1]?.location
  const currentRoute = yield (function () {
    return location?.pathname.toLowerCase() || false
  })()
  const previousRoute = yield (function () {
    return previous?.pathname.toLowerCase() || false
  })()

  if (!previousRoute && !/createbooking/i.test(currentRoute)) {
    // remove booking info from session storage when navigating to the other route from url
    window.sessionStorage.removeItem('booking_info')
  }

  if (currentRoute !== previousRoute) {
    // preserve state when navigating subroutes
    if (/ordermanagement/i.test(previousRoute) && !/ordermanagement/i.test(currentRoute)) {
      yield put(formikActions.doResetForm({ name: 'OrderFilterForm' }))
      yield put(doResetOrderManagement())
      yield put(doResetOperations())
    }
    // preserve state when navigating operation subroutes
    if (/operationmanagement/i.test(previousRoute) && !/operationmanagement/i.test(currentRoute)) {
      yield put(formikActions.doResetForm({ name: 'OperationFilterForm' }))
      yield put(doResetOperations())
    }
    // preserve state when navigating booking subroutes
    if (/bookingmanagement/i.test(previousRoute) && !/bookingmanagement/i.test(currentRoute)) {
      yield put(formikActions.doResetForm({ name: 'BookingFilterForm' }))
      yield put(doResetBookings())
    }
    // preserve state when navigating subscriptionplan subroutes
    if (/subscriptionplan/i.test(previousRoute) && !/subscriptionplan/i.test(currentRoute)) {
      yield put(formikActions.doResetForm({ name: 'SubscriptionPlanFilters' }))
      yield put(doResetOperations())
    }
    // preserve state when navigating createPayByLinkPlus subroutes
    if (/createpaybylinkplus/i.test(previousRoute) && !/createpaybylinkplus/i.test(currentRoute)) {
      yield put(formikActions.doResetForm({ name: 'PBLForm' }))
      yield put(doResetPblGenerateResult())
    }
    if (/terminalsmanagement/i.test(previousRoute) && !/terminalsmanagement/i.test(currentRoute)) {
      // reset active tab index when navigating away from /terminalsmanagement
      yield put(doSetActiveTabIndex(0))
    }
    // preserve state when navigating paytocard creation subroutes
    if (/paytocard/i.test(previousRoute) && !/paytocard/i.test(currentRoute)) {
      yield put(formikActions.doResetForm({ name: 'PTCForm' }))
      yield put(doResetPtcGenerateResult())
    }
    if (
      /disputeless/i.test(currentRoute) ||
      /postokenization/i.test(currentRoute) ||
      /clickncollect/i.test(currentRoute)
    ) {
      const terminalId = yield select(getSelectedTerminalId)
      if (!terminalId) {
        window.location.replace(`${basename}${routesList.terminalsManagement.goToTerminalsManagement}`)
      }
    }
  }
}
// @generator saga

// @generator saga:watch

export function* watchRouteChange() {
  yield takeLatest('@@router/LOCATION_CHANGE', routeChangeSaga)
}

/**
 * Root saga manages watcher lifecycle
 */
export default function* rootSaga() {
  yield all([
    watchRouteChange()
    // @generator saga:fork
  ])
}
